"use client";

import { UploadThumbnailActions } from "@saas/shared/assets/UploadThumbnailActions";
import { buildDisplayImageUrl } from "@saas/shared/lib/image-url-builder";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { FolderIcon, RefreshCwIcon, UploadIcon } from "lucide-react";
import { type DragEvent, useRef, useState } from "react";
import type { I2VInputImage } from "../hooks/use-image-to-video";

/**
 * 一个固定槽位的上传方块（首帧 / 尾帧）。空态是「Click / Drop」，有图时换成缩略图
 * 加 hover 操作条。reference tab 用的是网格，不走这里。
 *
 * 样式沿用站内已有的上传区写法（见 ai-video 的 dropzone）：白底 + 2px 虚线，
 * 图标坐在圆角灰块里，下面跟一个带文字的 outline 按钮。
 */
export function FrameSlot({
	image,
	title,
	subtitle,
	selectAssetLabel,
	className,
	compact,
	fit = "cover",
	disabled,
	locked = false,
	swapLabel,
	onFiles,
	onPickAsset,
	onPreview,
	onEdit,
	onRemove,
}: {
	image: I2VInputImage | null;
	title: string;
	subtitle?: string;
	selectAssetLabel: string;
	/** 高度由调用方决定：单槽位撑满，首尾帧两个并排时矮一些 */
	className?: string;
	/** 并排的窄槽位：图标和按钮都收一号，免得换行 */
	compact?: boolean;
	/**
	 * 缩略图怎么铺：默认裁满；contain 整张放下（Ozil Goal 只有一个槽、拉满一整行，
	 * 竖版全身照裁满只剩肚子那一截，看不到脸）
	 */
	fit?: "cover" | "contain";
	disabled?: boolean;
	/** 默认场景图只允许预览，不能修改或删除。 */
	locked?: boolean;
	/** 有图时底部的「换一张」文案；只给可换的默认图传 */
	swapLabel?: string;
	onFiles: (files: File[]) => void;
	onPickAsset: () => void;
	onPreview: () => void;
	onEdit: () => void;
	onRemove: () => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	const dropHandlers = {
		onDragOver: (event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (!disabled) {
				setIsDragOver(true);
			}
		},
		onDragLeave: () => setIsDragOver(false),
		onDrop: (event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setIsDragOver(false);
			if (disabled) {
				return;
			}
			onFiles(Array.from(event.dataTransfer.files));
		},
	};
	const fileInput = (
		<input
			ref={inputRef}
			type="file"
			accept="image/jpeg,image/png,image/webp"
			className="hidden"
			onChange={(event) => {
				onFiles(Array.from(event.target.files ?? []));
				event.target.value = "";
			}}
		/>
	);

	if (image) {
		// 可换的默认图（Tiny Desk 两位乐手）：删了会被填回去，所以不给删，
		// 底部常驻「Replace」点开选图，也能直接拖进来（同营销 hero 的 swapLabel）
		const swappable = Boolean(swapLabel) && !locked && !disabled;
		return (
			<div
				className={cn(
					"group relative w-full overflow-hidden rounded-2xl border border-border bg-muted/20",
					swappable && isDragOver && "border-blue-400",
					className,
				)}
				{...(swappable ? dropHandlers : {})}
			>
				{/* 绝对定位铺满：容器只有 min-height，图片用 h-full 会解析成 auto，
				    于是按原图比例把槽位撑开——首尾帧两个槽就会一高一矮 */}
				<img
					src={buildDisplayImageUrl(image.previewUrl, 640)}
					alt={title}
					className={cn(
						"absolute inset-0 h-full w-full",
						fit === "contain" ? "object-contain" : "object-cover",
					)}
				/>
				<UploadThumbnailActions
					onPreview={onPreview}
					onEdit={locked ? undefined : onEdit}
					onDelete={locked || swappable ? undefined : onRemove}
				/>
				{swappable ? (
					<>
						{fileInput}
						<button
							type="button"
							onClick={() => inputRef.current?.click()}
							aria-label={`${swapLabel}: ${title}`}
							className="absolute inset-x-2 bottom-2 flex items-center justify-center gap-1 rounded-full bg-black/60 py-1 font-medium text-white text-xs transition-colors hover:bg-black/80"
						>
							<RefreshCwIcon className="size-3" />
							{swapLabel}
						</button>
					</>
				) : null}
			</div>
		);
	}

	return (
		<div
			data-missing-field="image"
			className={cn(
				// 照 /ai-edit-image 的上传框：灰底 + 1px 细虚线 + 圆角 16
				"flex w-full flex-col items-center justify-center rounded-2xl border border-dashed p-3 text-center transition-colors",
				// 并排的两个槽内边距收一档：撑满的 sm:p-6 会让框比内容高出一大截
				compact ? "sm:p-4" : "sm:p-6",
				disabled
					? "cursor-not-allowed border-zinc-300 bg-zinc-100"
					: isDragOver
						? "cursor-pointer border-blue-400 bg-blue-50"
						: "cursor-pointer border-zinc-300 bg-zinc-100 hover:border-zinc-400 hover:bg-zinc-200/60",
				className,
			)}
			onClick={() => {
				if (!disabled) {
					inputRef.current?.click();
				}
			}}
			{...dropHandlers}
		>
			{fileInput}
			<button
				type="button"
				disabled={disabled}
				onClick={(event) => {
					event.stopPropagation();
					inputRef.current?.click();
				}}
				className="flex flex-col items-center"
			>
				<UploadIcon
					className={cn(
						"text-zinc-700",
						compact ? "mb-2 size-5" : "mb-3 size-7",
					)}
				/>
				{/* 独行时按 ai-video 的 dropzone 用 muted；下面还跟着副标题
				    （首尾帧）时才用深色，好把两行拉开层级 */}
				<span
					className={cn(
						"text-sm",
						subtitle ? "text-zinc-700" : "text-zinc-500",
					)}
				>
					{title}
				</span>
				{subtitle ? (
					<span className="mt-0.5 text-xs text-zinc-400">
						{subtitle}
					</span>
				) : null}
			</button>
			<Button
				type="button"
				variant="outline"
				size="sm"
				disabled={disabled}
				onClick={(event) => {
					event.stopPropagation();
					onPickAsset();
				}}
				// 次要入口，退到上传文案后面：透明底细框灰字；min-h-0 抵掉 Button sm 的 44（10/21 老公选 C）
				className={cn(
					"h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700",
					compact ? "mt-3 px-2.5" : "mt-4 px-3.5",
				)}
			>
				<FolderIcon className="h-3.5 w-3.5" />
				<span className="truncate">{selectAssetLabel}</span>
			</Button>
		</div>
	);
}
